import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  NgControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
} from '@ngneat/reactive-forms';
import * as _ from 'lodash';

import {
  FormValidators,
  NestedFormComponent,
} from '@digital-platform/shared/ui';

export interface Item {
  readonly name: string;
  readonly typicalOrActual: number;
  readonly min: number;
  readonly max: number;
  readonly unitOfMeasure: string;
}

@Component({
  selector: 'digital-platform-waste-component-row',
  templateUrl: './waste-component-row.component.html',
  styleUrls: ['./waste-component-row.component.scss'],
})
export class WasteComponentRowComponent
  extends NestedFormComponent<any>
  implements OnInit, OnChanges, ControlValueAccessor<any>
{
  @Input() showPpm = false;
  @Input() hideLabels = false;
  @Input() isRequired = false;
  @Input() isToggled = false;
  @Input() sum: number;
  @Input() labels: any;
  @Input() isLegacy = false;

  select = [
    { code: 'percent', label: ' %' },
    { code: 'ppm', label: 'ppm' },
  ];

  public onTouched: () => void;
  _onChange: (value: any) => void;

  public form: FormGroup<any>;

  public readonly DEFAULT_VALUE: Item = {
    name: null,
    typicalOrActual: null,
    min: null,
    max: null,
    unitOfMeasure: 'percent',
  };

  validators = {
    name: [
      FormValidators.requiredIf(() => this.form.get('typicalOrActual').value),
      FormValidators.requiredIf(() => this.form.get('min').value),
      FormValidators.requiredIf(() => !this.form.value.name && this.isRequired),
    ],
    typicalOrActual: [
      FormValidators.decimal(),
      FormValidators.requiredIf(
        () => this.form.get('name').value && this.isLegacy === false
      ),
      FormValidators.lessThan(() => {
        if (this.isLegacy === true) return null;
        if (this.form.get('unitOfMeasure').value === 'percent') {
          return 101; // lessThan fails if >= configured value
        }

        return null;
      }),
      () => {
        if (!this.form) {
          return null;
        }

        if (
          this.form.get('unitOfMeasure').value === 'percent' &&
          this.sum > 100
        ) {
          return {
            sumOver100: true,
          };
        }

        return null;
      },
    ],
    min: [
      this.mustBeNumeric(),
      Validators.max(100),
      this.mustBeLessThan('max'),
      FormValidators.requiredIf(
        () => this.isLegacy === false && this.form.get('max').value
      ),
      FormValidators.requiredIf(
        () => this.isLegacy === false && this.form.get('name').value
      ),
    ],
    max: [
      this.mustBeNumeric(),
      Validators.max(100),
      this.mustBeGreaterThan('min'),
      FormValidators.requiredIf(
        () => this.isLegacy === false && this.form.get('name').value
      ),
      FormValidators.requiredIf(
        () => this.isLegacy === false && this.form.get('min').value
      ),
    ],
  };

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private readonly fb: FormBuilder
  ) {
    super(controlDir);
    this.value = this.DEFAULT_VALUE;

    this.buildForm();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.form) {
      this.onToggleChange();
      if (
        !this.isToggled &&
        changes.sum?.currentValue !== changes.sum?.previousValue
      ) {
        this.onSumChange();
      }
      if (!changes.isRequired?.currentValue) {
        this.form.controls.name.removeValidators(Validators.required);
        this.form.updateValueAndValidity();
      } else {
        this.form.controls.name.addValidators(this.validators.name);
        this.form.updateValueAndValidity();
      }
    }
  }

  private onToggleChange() {
    if (this.form) {
      if (this.isToggled) {
        this.disableControls(['typicalOrActual']);
        this.enableControls(['min', 'max']);
        this.form.get('min').updateValueAndValidity();
        this.form.get('max').updateValueAndValidity();
      } else {
        this.enableControls(['typicalOrActual']);
        this.disableControls(['min', 'max']);
        this.form.get('typicalOrActual').updateValueAndValidity();
      }
    }
  }

  private onSumChange() {
    if (
      this.sum >= 100 &&
      !this.form?.value?.typicalOrActual &&
      this.form.value?.unitOfMeasure === 'percent'
    ) {
      this.form.get('name').setValue(null);
      this.form.disable({ emitEvent: false });
    } else {
      this.form.get('typicalOrActual').enable({ emitEvent: false });
      this.form.get('name').enable({ emitEvent: false });
      this.form.get('unitOfMeasure').enable({ emitEvent: false });
    }
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit() {
    super.ngOnInit();

    if (!this.form) {
      this.buildForm();
    }
  }

  enableControls = (controls: string[]) =>
    controls.forEach((f) => {
      const control = this.form.get(f);
      control.enable({ emitEvent: false });
    });

  disableControls = (controls: string[]) =>
    controls.forEach((f) => {
      const control = this.form.get(f);
      control.setValue(null, { emitEvent: false });
      control.disable({ emitEvent: false });
    });

  private buildForm() {
    const value = this.value;

    this.form = this.fb.group({
      name:
        this.isLegacy === false
          ? [value.name, this.validators.name]
          : [value.name],
      typicalOrActual:
        this.isLegacy === false
          ? [
              {
                value: value.typicalOrActual,
                disabled: this.ifDisable(value.typicalOrActual),
              },
              this.validators.typicalOrActual,
            ]
          : [
              {
                value: value.typicalOrActual,
                disabled: this.ifDisable(value.typicalOrActual),
              },
            ],
      min:
        this.isLegacy === false
          ? [
              {
                value: value.min,
                disabled: this.ifDisable(value.min),
              },
              this.validators.min,
            ]
          : [
              {
                value: value.min,
                disabled: this.ifDisable(value.min),
              },
            ],
      max:
        this.isLegacy === false
          ? [
              {
                value: value.max,
                disabled: this.ifDisable(value.max),
              },
              this.validators.max,
            ]
          : [
              {
                value: value.max,
                disabled: this.ifDisable(value.max),
              },
            ],
      unitOfMeasure: [value.unitOfMeasure],
    });
    const name = this.form.get('name');
    const typicalOrActual = this.form.get('typicalOrActual');
    const min = this.form.get('min');
    const max = this.form.get('max');
    this.form
      .select((state) => state.name)
      .subscribe((n) => {
        if (!n) {
          name.updateValueAndValidity();
        }
        min.updateValueAndValidity();
        max.updateValueAndValidity();
        typicalOrActual.updateValueAndValidity();
      });
    this.form
      .select((state) => state.typicalOrActual)
      .subscribe(() => {
        name.updateValueAndValidity();
      });
    this.form
      .select((state) => state.min)
      .subscribe(() => {
        name.updateValueAndValidity();
        max.updateValueAndValidity();
      });
    this.form
      .select((state) => state.max)
      .subscribe(() => {
        name.updateValueAndValidity();
        min.updateValueAndValidity();
      });

    if (this.isLegacy === true) {
      this.form.clearValidators();
    }
    this.form.updateValueAndValidity();
  }

  ifDisable = (value: any) => (value ? true : false);

  private mustBeGreaterThan<T>(field: string): ValidatorFn {
    return (control) => {
      if (!control.parent) {
        return null;
      }

      const value = control.parent.get(field)?.value;
      if (value !== null) {
        return Validators.min(+value + 1)(control);
      }

      return null;
    };
  }

  private mustBeLessThan<T>(field: string): ValidatorFn {
    return (control) => {
      if (!control.parent) {
        return null;
      }

      const value = control.parent.get(field).value;
      if (value !== null) {
        return Validators.max(+value)(control);
      }

      return null;
    };
  }
  private mustBeNumeric(): ValidatorFn {
    const validatorFn = (control: AbstractControl) => {
      const validationErrors = Validators.pattern('-?[0-9]+')(control);

      if (validationErrors?.pattern) {
        return {
          numeric: {
            ...validationErrors.pattern,
          },
        };
      }

      return null;
    };

    return validatorFn as ValidatorFn;
  }

  public errorHandling(formControlName) {
    const errors = this.form.controls[formControlName]?.errors;
    return errors && Object.keys(errors).length > 0;
  }

  public get physicalComponentSamples(): FormArray<any> {
    return this.form.get('physicalComponents') as FormArray<any>;
  }

  public getSampleErrorMessage(
    formControlName: string,
    label: string
  ): string | void {
    const errors = this.form.controls[formControlName]?.errors;
    const firstErrorKey = Object.keys(errors)[0];
    const firstError = errors[firstErrorKey];
    switch (firstErrorKey) {
      case 'required':
        return `${label} is required`;
      case 'min':
        return `${label} must be greater than Low %`;
      case 'max':
        return `${label} must be less than ${firstError['max']}`;
      case 'numeric':
        return `${label} must be a whole number`;
      case 'decimal':
        return `${label} must be a Number`;
      default:
        console.log(`Unknown error: ${firstErrorKey}`);
    }
  }
}
